import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Loader2, AlertTriangle, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInventoryPage() {
  const [products, setProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [movementData, setMovementData] = useState({ quantity_change: 0, movement_type: 'in', reason: '' });
  const [saving, setSaving] = useState(false);
  const [customFilter, setCustomFilter] = useState('all');

  useEffect(() => { fetchData(); }, [searchQuery, customFilter]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({ is_custom: customFilter });
      if (searchQuery) params.append('search', searchQuery);
      const [productsRes, lowStockRes] = await Promise.all([
        axios.get(`${API}/products?${params}`),
        axios.get(`${API}/inventory/low-stock`)
      ]);
      setProducts(productsRes.data);
      setLowStockProducts(lowStockRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const openMovementModal = (product, variantIndex = 0) => {
    setSelectedProduct(product);
    setSelectedVariantIndex(variantIndex);
    setMovementData({ quantity_change: 0, movement_type: 'in', reason: '' });
    setShowMovementModal(true);
  };

  const handleMovement = async () => {
    if (!movementData.reason) { toast.error('Ingresa una razón'); return; }
    if (movementData.quantity_change === 0) { toast.error('La cantidad no puede ser 0'); return; }

    setSaving(true);
    try {
      const change = movementData.movement_type === 'out' ? -Math.abs(movementData.quantity_change) : Math.abs(movementData.quantity_change);
      await axios.post(`${API}/inventory/movement`, {
        product_id: selectedProduct.id,
        variant_index: selectedVariantIndex,
        quantity_change: change,
        movement_type: movementData.movement_type,
        reason: movementData.reason
      });
      toast.success('Inventario actualizado');
      setShowMovementModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <div>
        <h1 className="text-2xl font-serif text-white">Inventario</h1>
        <p className="text-white/60 text-sm">Gestión de stock y movimientos</p>
      </div>

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <div className="p-4 bg-red-900/20 border border-red-500/30 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-500 font-medium">¡Alerta de Stock Bajo!</h3>
            <p className="text-white/60 text-sm mt-1">
              {lowStockProducts.length} producto(s) tienen stock bajo o están agotados.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {lowStockProducts.slice(0, 5).map((p) => (
                <span key={p.id} className="px-2 py-1 bg-red-500/20 text-red-400 text-xs">
                  {p.name} ({p.total_stock})
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search & Filter */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar productos..."
            className="pl-10 bg-[#121212] border-white/10 text-white"
          />
        </div>
        <div className="flex gap-2">
          {[
            { value: 'all', label: 'Todos' },
            { value: 'false', label: 'Tienda' },
            { value: 'true', label: 'Personalizados' }
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setCustomFilter(opt.value)}
              className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                customFilter === opt.value
                  ? 'bg-gold/20 text-gold border border-gold/30'
                  : 'bg-white/5 text-white/60 border border-white/10 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Products with Inventory */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>
      ) : (
        <div className="space-y-4">
          {products.map((product) => (
            <div key={product.id} className="bg-[#121212] border border-white/10 p-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-20 bg-white/5 flex-shrink-0 overflow-hidden">
                    {product.images?.[0] ? (
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">Sin img</div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium">{product.name}</h3>
                      {product.is_custom && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-500/20 text-purple-400">
                          Personalizado
                        </span>
                      )}
                    </div>
                    <p className="text-white/40 text-sm">{product.sku || 'Sin SKU'}</p>
                    <p className={`text-sm font-medium mt-1 ${product.total_stock <= 5 ? 'text-red-500' : 'text-green-500'}`}>
                      Stock Total: {product.total_stock}
                    </p>
                  </div>
                </div>
              </div>

              {/* Variants */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-2 text-left text-white/60 font-medium">Variante</th>
                      <th className="py-2 text-center text-white/60 font-medium">Stock</th>
                      <th className="py-2 text-right text-white/60 font-medium">Ajustar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.variants?.map((variant, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="py-3">
                          <span className="text-white">{variant.size}</span>
                          <span className="text-white/40 mx-2">|</span>
                          <span className="text-white/60">{variant.color}</span>
                        </td>
                        <td className="py-3 text-center">
                          <span className={`font-medium ${variant.stock_quantity <= 5 ? 'text-red-500' : 'text-white'}`}>
                            {variant.stock_quantity}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <button
                            onClick={() => openMovementModal(product, i)}
                            className="px-3 py-1 bg-gold/10 text-gold text-xs hover:bg-gold/20 transition-colors"
                          >
                            Ajustar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Movement Modal */}
      <Dialog open={showMovementModal} onOpenChange={setShowMovementModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Ajustar Inventario</DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-white/5">
                <p className="text-white font-medium">{selectedProduct.name}</p>
                <p className="text-white/60 text-sm">
                  {selectedProduct.variants?.[selectedVariantIndex]?.size} | {selectedProduct.variants?.[selectedVariantIndex]?.color}
                </p>
                <p className="text-gold mt-2">
                  Stock Actual: {selectedProduct.variants?.[selectedVariantIndex]?.stock_quantity}
                </p>
              </div>

              <div>
                <label className="text-white/60 text-sm">Tipo de Movimiento</label>
                <Select value={movementData.movement_type} onValueChange={(v) => setMovementData({ ...movementData, movement_type: v })}>
                  <SelectTrigger className="bg-black border-white/20 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/20">
                    <SelectItem value="in" className="text-white">Entrada (+ stock)</SelectItem>
                    <SelectItem value="out" className="text-white">Salida (- stock)</SelectItem>
                    <SelectItem value="adjustment" className="text-white">Ajuste</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-white/60 text-sm">Cantidad</label>
                <Input
                  type="number"
                  min="0"
                  value={movementData.quantity_change}
                  onChange={(e) => setMovementData({ ...movementData, quantity_change: parseInt(e.target.value) || 0 })}
                  className="bg-black border-white/20 text-white mt-1"
                />
              </div>

              <div>
                <label className="text-white/60 text-sm">Razón *</label>
                <Textarea
                  value={movementData.reason}
                  onChange={(e) => setMovementData({ ...movementData, reason: e.target.value })}
                  className="bg-black border-white/20 text-white mt-1"
                  placeholder="Ej: Nueva mercancía, Venta, Ajuste por inventario..."
                  rows={3}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowMovementModal(false)} className="flex-1 border-white/20 text-white hover:bg-white/5">
                  Cancelar
                </Button>
                <Button onClick={handleMovement} disabled={saving} className="flex-1 btn-gold-solid">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
